<template>
  <div class="absolute bottom-0 left-0 right-0 mx-3 my-4 flex items-center gap-3 lg:gap-2 text-sm pointer-events-none">
    <div v-if="accountStore.isLoggedIn" class="lg:ml-auto"/>
    <button v-else
            type="button"
            @click="() => updateShowLogIn(true)"
            :style="twRingColors"
            class="rounded-md relative group overflow-hidden focus:outline-none pointer-events-auto focus:ring-2 focus:ring-offset-2 flex items-center justify-center lg:gap-1 lg:h-10 lg:px-3 flex-shrink-0 mr-auto">
        <span class="inset-0 absolute opacity-20 group-hover:opacity-30 pointer-events-none transition-all"
              :style="invertedColors"/>
      <UserIcon class="w-12 h-12 p-3 lg:h-4 lg:w-4 lg:p-0"/>
      <span class="hidden lg:block font-semibold">Log In</span>
    </button>
    <button type="button"
            v-if="!props.prevStepHidden"
            @click="prevStep"
            :style="twRingColors"
            :disabled="prevStepDisabled"
            :class="prevStepDisabled && 'opacity-50'"
            class="rounded-md relative group overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 flex-shrink-0">
        <span class="inset-0 absolute opacity-20 group-hover:opacity-30 pointer-events-none transition-all"
              :style="invertedColors"/>
      <ChevronUpIcon class="w-12 h-12 lg:w-10 lg:h-10 p-2 lg:p-1"/>
    </button>
    <button type="button"
            v-if="!props.nextStepHidden"
            @click="nextStep"
            :style="twRingColors"
            :disabled="nextStepDisabled"
            :class="nextStepDisabled && 'opacity-50'"
            class="rounded-md relative group overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 block sm:mr-auto md:mr-0 flex-shrink-0">
        <span class="inset-0 absolute opacity-20 group-hover:opacity-30 pointer-events-none transition-all"
              :style="invertedColors"/>
      <ChevronDownIcon class="w-12 h-12 lg:w-10 lg:h-10 p-2 lg:p-1"/>
    </button>
    <a v-if="!hidePoweredByBusinessDraft"
       href="https://businessdraft.com" target="_blank" :style="twRingColors"
       class="opacity-80 hover:opacity-100 px-2 py-1 flex items-center justify-center ml-auto sm:ml-0 h-12 lg:h-10 lg:px-4 rounded-md relative group overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2">
        <span class="inset-0 absolute opacity-20 group-hover:opacity-30 pointer-events-none transition-all "
              :style="invertedColors"/>
      <span class="text-xs xl:text-base">
        Powered by
        <br class="xl:hidden"/>
        <b>Business Draft</b>
      </span>
    </a>
  </div>
</template>

<script setup>
import {ChevronDownIcon, ChevronUpIcon, UserIcon} from "@heroicons/vue/20/solid/index.js";
import {computed, inject, onBeforeUnmount, onMounted, ref} from "vue";
import {useAccountStore} from "../stores/AccountStore";

const emits = defineEmits(["nextStep", "prevStep", "update:showLogIn"]);
const props = defineProps({
  nextStepDisabled: Boolean,
  prevStepDisabled: Boolean,
  nextStepHidden: Boolean,
  prevStepHidden: Boolean,
});

const typeformData = inject("typeformData", {});
const accountStore = useAccountStore();

const debounceShortcuts = ref(false);

const invertedColors = computed(() => `background: ${typeformData.primaryForeground};color: ${typeformData.primaryBackground};`);
const twRingColors = computed(() => `--tw-ring-offset-color:${typeformData.primaryBackground};--tw-ring-color:${typeformData.primaryForeground};`);

const hidePoweredByBusinessDraft = inject("hidePoweredByBusinessDraft", false);

const handleEnter = (e) => {
  if (e.key !== "Enter" || e.target.tagName === "TEXTAREA") return;
  nextStep();
  window.dispatchEvent(new Event("typeform:enter"));
  debounceShortcuts.value = true;
};

const handleArrowKeys = (e) => {
  if (e.key !== "ArrowUp" && e.key !== "ArrowDown" && e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;

  switch (e.key) {
  case "ArrowUp":
  case "ArrowLeft":
    return prevStep();
  case "ArrowDown":
  case "ArrowRight":
    return nextStep();
  }

  debounceShortcuts.value = true;
};

const nextStep = () => emits("nextStep");
const prevStep = () => !props.prevStepHidden && !props.prevStepDisabled && emits("prevStep");
const updateShowLogIn = (value) => emits("update:showLogIn", value);

const handleShortcuts = (e) => {
  if (debounceShortcuts.value) return;

  handleEnter(e);
  handleArrowKeys(e);

  if (debounceShortcuts.value) {
    setTimeout(() => debounceShortcuts.value = false, 300);
  }
};

onMounted(() => {
  window.addEventListener("keydown", handleShortcuts);
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", handleShortcuts);
});
</script>

<style scoped>
.pointer-events-none button,
.pointer-events-none a
{
  pointer-events: auto;
}
</style>
